<template>
    <div
        class="flex flex-col items-start gap-2 flex-1 px-4 py-5 sm:p-4 md:rounded-lg"
    >
        <dt class="truncate text-lg font-medium text-slate-800 dark:text-slate-100 py-1 mb-1">
            Total annual fees
        </dt>
        <dashboard-stat-skeleton v-if="loading" />
        <template v-else>
            <dd class="mt-1 text-3xl font-medium text-slate-800 dark:text-slate-100 mb-1">
                <template v-if="annualFees > 0">
                    <count-up
                        :end-val="annualFees"
                        :options="{ prefix: '$' }"
                    ></count-up>
                </template>
                <template v-else>
                    <hr
                        class="w-4 border-slate-800 dark:border-slate-100 border-b-2 rounded-md text-3xl font-semibold text-slate-900 dark:text-slate-100 mb-3 mt-6"
                    />
                </template>
            </dd>
            <dt class="text-sm font-medium text-slate-500 dark:text-slate-400">
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <span>Annual fees</span>
                        <template v-if="upcomingFees.length > 0">
                            <div
                                class="flex items-center gap-1 cursor-pointer text-orange hover:text-orange-800 focus:text-orange-800 focus:outline-none transition-colors relative ml-2"
                                @click="togglePopover"
                                @keydown.enter="togglePopover"
                                @keydown.space.prevent="togglePopover"
                                tabindex="0"
                                role="button"
                                ref="popoverTrigger"
                                id="hs-dashboard-upcoming-annual-fees-detail"
                            >
                                <span class="font-medium text-xs">
                                    ({{ upcomingFees.length }}
                                    {{
                                        upcomingFees.length === 1
                                            ? 'fee'
                                            : 'fees'
                                    }}
                                    due soon)
                                </span>
                                <Icon
                                    icon="arrow-top"
                                    class="text-orange-600"
                                />
                            </div>
                            <Teleport to="body">
                                <div
                                    v-if="isPopoverOpen"
                                    ref="popoverContent"
                                    :style="floatingStyles"
                                    class="bg-white dark:bg-slate-800 rounded-md border border-slate-200 p-2 min-w-[300px] shadow-lg z-[9999]"
                                >
                                    <div
                                        v-for="fee in upcomingFees"
                                        :key="fee.id"
                                        class="flex items-center justify-between gap-3 p-2 rounded-md card-cursor"
                                    >
                                        <div class="flex items-center gap-3">
                                            <img
                                                :src="getCardImageUrl(fee)"
                                                alt="card image"
                                                class="w-[76px] h-12 rounded-sm object-cover shadow-sm"
                                            />
                                            <div class="flex flex-col">
                                                <div
                                                    class="text-sm font-medium text-gray-900 dark:text-gray-100 text-left"
                                                >
                                                    {{ fee.card_title }}
                                                    <span
                                                        v-if="fee.last_four"
                                                        class="ml-1"
                                                    >
                                                        {{
                                                            `(${fee.last_four})`
                                                        }}
                                                    </span>
                                                </div>
                                                <div
                                                    class="text-sm text-gray-500 dark:text-gray-400 text-left"
                                                >
                                                    Due {{ fee.due_date }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="text-right">
                                            <div
                                                class="text-sm font-semibold text-gray-900 dark:text-gray-100"
                                            >
                                                ${{ fee.fee_amount }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Teleport>
                        </template>
                    </div>
                </div>
            </dt>
        </template>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import CountUp from 'vue-countup-v3';
import { ref, onMounted, onUnmounted } from 'vue';
import {
    useFloating,
    autoUpdate,
    offset as offsetMiddleware,
    flip,
    shift,
} from '@floating-ui/vue';
import Icon from '@/components/ui/icon/icon.vue';
import DashboardStatSkeleton from './dashboard-stat-skeleton.vue';

export default {
    props: [],
    components: {
        CountUp,
        Icon,
        DashboardStatSkeleton,
    },
    setup() {
        const cdnUrl = ref('');
        const isPopoverOpen = ref(false);
        const popoverTrigger = ref(null);
        const popoverContent = ref(null);

        const { floatingStyles } = useFloating(
            popoverTrigger,
            popoverContent,
            {
                strategy: 'fixed',
                placement: 'bottom-end',
                whileElementsMounted: autoUpdate,
                middleware: [
                    offsetMiddleware(8),
                    flip({ padding: 8 }),
                    shift({ padding: 8 }),
                ],
            }
        );

        const togglePopover = () => {
            isPopoverOpen.value = !isPopoverOpen.value;
        };

        const handleOutsideClick = (event) => {
            if (
                popoverContent.value &&
                !popoverContent.value.contains(event.target) &&
                popoverTrigger.value &&
                !popoverTrigger.value.contains(event.target)
            ) {
                isPopoverOpen.value = false;
            }
        };

        const getCardImageUrl = (fee) => {
            if (fee.card_image?.file_name) {
                return `${cdnUrl.value}/${fee.card_image.path}/_small/${fee.card_image.file_name}`;
            }
            return '/images/cards/default.png';
        };

        onMounted(() => {
            document.addEventListener('click', handleOutsideClick);
            cdnUrl.value = window.cdnUrl;
        });

        onUnmounted(() => {
            document.removeEventListener('click', handleOutsideClick);
        });

        return {
            isPopoverOpen,
            popoverTrigger,
            popoverContent,
            floatingStyles,
            togglePopover,
            getCardImageUrl,
        };
    },
    computed: {
        ...mapState(useDashboardStore, [
            'loading',
            'annualFees',
            'upcomingFees',
        ]),
    },
    data: () => ({}),
    methods: {},
    created() {},
    mounted() {},
};
</script>

<style lang="scss" scoped>
.card-cursor {
    cursor: default;
    &:hover {
        background-color: unset;
    }
}
</style>
